/**
 * ★ THE SEAM. This is the ONE place in the cabinet that decides whether a call to `authors-api`
 * goes to the network or to the fixture. Nothing else in the app imports `./fetch-transport` or
 * `./mock-transport`, and nothing else calls `fetch`.
 *
 * IT FOLLOWS THE APP'S ONE LIVE FLAG. `isLiveApiConfigured()` in src/config.ts reads
 * `VITE_AUTHORS_API_LIVE`, and so does every other transport in this app. This file used to read a
 * SECOND variable of its own, `VITE_AUTHORS_API_TRANSPORT=live`, which let a build send the
 * hand-off and `GET /v1/me` to the fixture while the cabinet screens went to the network - the
 * person would be "signed in" by the mock and then refused by the real API on every screen. One
 * flag makes that combination unbuildable. Every screen, every hook and every typed operation above
 * this line is written against the real contract and does not move.
 *
 * WHY THE DEFAULT IS THE MOCK AND NOT THE NETWORK. The default build is the one the accessibility
 * leg checks, and it checks it with every seam on its fixture (`scripts/a11y-roots.mjs`, "THE
 * SPA SEAM DEFAULTS TO FIXTURES. THE LEG NEEDS NO LIVE API"). `dev:stack` in this app's
 * package.json is the build that turns the flag on, against the local stack.
 */
import { isLiveApiConfigured } from '../../../config';
import { fetchTransport } from './fetch-transport';
import { applyMockScenarioFromSearch, mockTransport } from './mock-transport';
import type { Transport } from './http';

export type TransportMode = 'mock' | 'live';

/** The flag, read at call time so a spec can flip it; a build inlines it. */
export function readTransportMode(): TransportMode {
  return isLiveApiConfigured() ? 'live' : 'mock';
}

export const AUTHORS_API_TRANSPORT_MODE: TransportMode = readTransportMode();

/**
 * The `?mx-mock=` debug switch is honoured ONLY while the fixture is in use. In a live build it is
 * inert, so a link carrying it cannot change how the real app behaves.
 */
if (AUTHORS_API_TRANSPORT_MODE === 'mock' && typeof window !== 'undefined') {
  applyMockScenarioFromSearch(window.location.search);
}

/** The choice itself, as a function so a spec can assert it under both values of the flag. */
export function selectAuthorsApiTransport(mode: TransportMode = readTransportMode()): Transport {
  return mode === 'live' ? fetchTransport : mockTransport;
}

export const authorsApiTransport: Transport = selectAuthorsApiTransport(AUTHORS_API_TRANSPORT_MODE);
